import { useMemo } from "react";
import { flattenMessagesPages, useMessages } from "@api/chat.api";
import { MessageType, type Message } from "@common/types/chat";
import {
    resolveMessageAuthor,
    type MessageAuthor,
} from "@components/mainInterface/chat/messageAuthor";
import { useIsFirmMemberOrStandaloneUser } from "@hooks/useIsFirmMemberOrStandaloneUser";
import { useUser } from "@hooks/useUser";

type ParticipantAuthor = Exclude<MessageAuthor, { kind: "api" }>;

const getAuthorKey = (author: ParticipantAuthor) =>
    author.kind === "user" ? `user-${author.user.id}` : author.kind;

interface GetThreadParticipantsParams {
    /** Oldest first. */
    messages: ReadonlyArray<
        Pick<Message, "agentChannel" | "user" | "isDeleted" | "type">
    >;
    canSeeAgentIdentity: boolean;
    currentUserId: number | undefined;
}

/**
 * The authors of `messages` other than the current user, once each in the
 * order they first posted. Deleted messages, audit-log entries (task
 * changes the backend records in the thread) and Platform API posts don't
 * count.
 */
export const getThreadParticipants = ({
    messages,
    canSeeAgentIdentity,
    currentUserId,
}: GetThreadParticipantsParams) => {
    const authorsByKey = new Map<string, ParticipantAuthor>();
    for (const message of messages) {
        const author = resolveMessageAuthor({ message, canSeeAgentIdentity });
        if (author.kind === "api") {
            continue;
        }
        const isCurrentUser =
            author.kind === "user" && author.user.id === currentUserId;
        if (
            message.isDeleted ||
            message.type === MessageType.AUDIT_LOGS ||
            isCurrentUser
        ) {
            continue;
        }
        const key = getAuthorKey(author);
        if (!authorsByKey.has(key)) {
            authorsByKey.set(key, author);
        }
    }
    return [...authorsByKey.values()];
};

/**
 * Everyone but the current user who posted in a task's thread (people, Kick,
 * the Kick agent), in the order they first posted. Reads the loaded pages of
 * the same messages query the chat uses, so it adds no request when the chat
 * is on screen; authors only in older, unloaded pages are left out.
 */
export const useTaskThreadParticipants = (threadId: string | undefined) => {
    const { data } = useMessages(threadId);
    const canSeeAgentIdentity = useIsFirmMemberOrStandaloneUser();
    const currentUserId = useUser()?.id;

    return useMemo(
        () =>
            getThreadParticipants({
                messages: flattenMessagesPages(data?.pages),
                canSeeAgentIdentity,
                currentUserId,
            }),
        [data?.pages, canSeeAgentIdentity, currentUserId],
    );
};
